import AddIcon from '@mui/icons-material/Add';
import { Button, Container, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import 'dayjs/locale/en-gb';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../../assets/images/042-market-research1.svg';
import {
  ActivityType,
  clearSelectedActivity,
  selectActivity,
  selectActivityList,
  selectSelectedActivity,
  setLoadInProgress,
} from '../../redux/reducers/activityReducer';
import { clearSelectedFinding, selectSelectedFinding, setSelectedFinding } from '../../redux/reducers/findingsReducer';
import { selectContactList, selectUserList } from '../../redux/reducers/organizationReducer';
import formatDate from '../../utils/dateHelpers';
import ActivityModal from '../activities/ActivityModal';
import CorrectiveActionModal from '../activities/CorrectiveActionModal';
import UpdateActivityModal from '../activities/UpdateActivityModal';

function ManagementActivities() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ModalList = Object.freeze({
    CLOSED: 'closed',
    CREATE: 'create',
    EDIT: 'edit',
    UPDATE: 'update',
  });

  const activities = useSelector(selectActivityList);
  const contactList = useSelector(selectContactList);
  const userList = useSelector(selectUserList);
  const selectedActivity = useSelector(selectSelectedActivity);
  const finding = useSelector(selectSelectedFinding);
  const [showModal, setShowModal] = useState(ModalList.CLOSED);

  const handleOpenModal = (modalName) => {
    setShowModal(modalName);
  };

  const handleCloseModal = () => {
    setShowModal(ModalList.CLOSED);
  };

  function findNames(idList) {
    const list = idList.map((id) => {
      const name = contactList.find((contact) => contact.id === id)?.name
        ?? userList.find((user) => user.id === id)?.firstName;
      return name;
    });
    return list.join(', ');
  }

  const handleSelectionChange = (event) => {
    if (event.length > 0) {
      dispatch(setLoadInProgress());
      dispatch(selectActivity(event[0]));
      handleOpenModal(ModalList.UPDATE);
    }
  };

  useEffect(() => {
    if (selectedActivity.findingId === '') {
      dispatch(clearSelectedFinding());
    } else {
      dispatch(setSelectedFinding(selectedActivity.findingId));
    }
  }, [selectedActivity]);

  const handleAddNewActivity = () => {
    dispatch(clearSelectedActivity());
    handleOpenModal(ModalList.CREATE);
  };

  const columns = [
    {
      field: 'activityStatus',
      headerName: t('activityManagement.field.activityStatus'),
      width: 200,
      editable: false,
      sortable: true,
    },
    {
      field: 'activityType',
      headerName: t('activityManagement.field.activityType'),
      width: 200,
      editable: false,
      sortable: true,
    },
    {
      field: 'title',
      headerName: t('activityManagement.field.title'),
      width: 400,
      editable: false,
      sortable: true,
    },
    {
      field: 'activityOwner',
      headerName: t('activityManagement.field.activityOwner'),
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: 'deadline',
      headerName: t('activityManagement.field.deadline'),
      width: 100,
      editable: false,
      sortable: true,
    },
    {
      field: 'created',
      headerName: t('activityManagement.field.created'),
      width: 100,
      editable: false,
      sortable: true,
    },
  ];

  const rows = activities.map((act) => ({
    id: act.id,
    activityStatus: t(`activity.status.${act.activityStatus}`),
    activityType: t(`activity.type.${act.activityType}`),
    title: act.title,
    activityOwner: findNames(act.activityOwner),
    deadline: formatDate(act.deadline),
    created: formatDate(act.created),
  }));

  return (
    <>
      {(showModal === ModalList.CREATE || showModal === ModalList.EDIT) && (
        selectedActivity.activityType === ActivityType.CORRECTIVE_ACTION ? (
          <CorrectiveActionModal
            handleClose={handleCloseModal}
            finding={finding}
          />
        ) : (
          <ActivityModal
            handleClose={handleCloseModal}
            activity={selectedActivity}
          />
        )
      )}
      {showModal === ModalList.UPDATE && (
        <UpdateActivityModal
          handleOpen={handleOpenModal}
          handleClose={handleCloseModal}
          activity={selectedActivity}
          modalList={ModalList}
        />
      )}
      <Container disableGutters maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <Stack spacing={4} my={4} variant="headerRow" direction="row">
          <Typography variant="tabHeader">{t('activityManagement.allActivities')}</Typography>
          <Button
            size="medium"
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => handleAddNewActivity()}
            sx={{ display: activities.length === 0 ? 'none' : '' }}
          >
            <Typography>{t('activityManagement.buttonCreateActivity')}</Typography>
          </Button>
        </Stack>
        {activities.length !== 0 ? (
          <DataGrid
            sx={{
              maxWidth: '1200px',
              border: 0,
              '.MuiDataGrid-columnHeaderTitle': {
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0.17px',
              },
              '.MuiDataGrid-cell': {
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20.02px',
                letterSpacing: '0.17px',
              },
            }}
            columns={columns}
            rows={rows}
            onRowSelectionModelChange={handleSelectionChange}
          />
        ) : (
          <Stack direction="column" variant="assessmentHeader" useFlexGap>
            <Typography variant="noActivities">{t('activityManagement.noActivitiesYet')}</Typography>
            <img src={logo} />
            <Button
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              onClick={() => handleAddNewActivity()}
            >
              <Typography>{t('activityManagement.buttonCreateFirstActivity')}</Typography>
            </Button>
          </Stack>
        )}
      </Container>
    </>
  );
}

export default ManagementActivities;
